import * as React from 'react'
import styled from 'styled-components'
import ReactModal from 'react-modal'

import Icon from '../../ui/Icon/Icon'
import ActionButton from '../../ui/ActionButton/ActionButton'

import media, { screenSizes } from '../../../services/generateMediaQueries'

const isDesktopFullImage = (type, desktop) => type === 0 && desktop

const StyledReactModal = styled(ReactModal)`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  transition: all var(--animation-duration) var(--animation-easing);

  &.ReactModal__Content {
    &--after-open {
      opacity: 1;
      transition: all var(--animation-duration) var(--animation-easing);
    }

    &--before-close {
      opacity: 0;
      transition: all calc(var(--animation-duration) / 2)
        var(--animation-easing);
    }
  }
`

const ModalContentWrapper = styled.div`
  overflow: auto;
  ${({ type, desktop }) =>
    !isDesktopFullImage(type, desktop) &&
    `
    max-height: 100vh;
    width: 100%;
    `}

  ${media.lessThan('small')`
    max-height: unset;
    height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
  `}
`

const ModalContent = styled.div`
  flex: 0 0 auto;
  position: relative;
  margin: 1.5rem auto;
  height: ${props =>
    isDesktopFullImage(props.type, props.desktop) ? 'unset' : '100%'};
  width: 100%;
  padding: 1.5rem;
  max-width: ${props =>
    props.type ? '398px' : props.desktop ? 'unset' : '300px'};

  ${media.lessThan('small')`
    height: unset;
    margin: ${props => (props.type === 2 ? '0' : '1.5rem auto')};
    padding: ${props => (props.type === 2 ? '0' : '1.5rem')};
    max-width: ${props => {
      if (props.type === 0) {
        return '300px'
      }
      if (props.type === 1) {
        return '398px'
      }
      return '100%'
    }};
  `}
`

const Content = styled.div`
  border-radius: 8px;
  overflow: hidden;
  height: ${({ type, desktop }) =>
    isDesktopFullImage(type, desktop) ? '80vh' : '100%'};
  width: 100%;
  background: #ffffff;

  ${media.lessThan('medium')`
    ${({ type, desktop }) =>
      isDesktopFullImage(type, desktop) && 'height:unset;'}
  `}

  ${media.lessThan('small')`

  height: ${({ type, desktop }) =>
    isDesktopFullImage(type, desktop) && 'unset'};
    overflow: auto;
    border-radius: ${props => (props.type === 2 ? '0' : '8px')};
    background-color: ${props =>
      props.type === 2 ? 'var(--color-primary)' : '#ffffff'};
    ${props => (props.type === 2 ? 'color: #ffffff;' : '')}
  `}
`

const DefaultCloseButton = styled.button`
  cursor: pointer;
  position: absolute;
  border: none;
  top: 0.75rem;
  right: 0.75rem;
  font-size: 1rem;
  background: #ffffff;
  width: 1.5rem;
  height: 1.5rem;
  padding: 5px;
  border-radius: 100%;
  outline: none;
  z-index: 1000;

  ${media.lessThan('small')`
    display: block;
    top: ${props => (props.type === 2 ? 'auto' : '0.75rem')};
    bottom: ${props => (props.type === 2 ? '-0.75rem' : 'auto')};
  `}

  > svg {
    vertical-align: baseline;
    min-width: unset;
    height: unset;
  }
`

const Image = styled.img`
  display: block;
  width: 100%;
  ${({ desktop }) =>
    desktop &&
    `
    height: 100%;
    object-fit: contain;
  ${media.lessThan('small')`
  height: unset;
  `}
    `}
`

const FullImageNote = styled.p`
  text-align: center;
  font-size: 0.75rem;
  color: #fff;
`

const DetailsContainer = styled.div`
  padding: 1.5rem;
`

const Title = styled.h1`
  font-size: 1rem;
  margin: 0;
  margin-bottom: 1.5rem;
`

const TextOnlyTitle = styled(Title)`
  margin-top: 1.5rem;
`

const Description = styled.p`
  font-size: 0.8rem;
  margin: 0;
  margin-bottom: 1.5rem;
`

const TextOnlyDescription = styled(Description)`
  margin-bottom: 3rem;
`

const BlockActionButton = styled(ActionButton)`
  width: 0;
  flex: auto;
`

const CloseButton = styled(BlockActionButton)`
  border: 0;
  background: transparent;
  margin: 0;
`

const TextOnlyViewButton = styled(BlockActionButton)`
  position: relative;
  ${media.lessThan('small')`
    border: 0;
    border-radius: 0;
    color: var(--color-primary);
    ::before {
      background-color: #fff;
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  `}
`

const CustomCtaSpacer = styled.div`
  width: 1rem;
  ${media.lessThan('small')`
    display: none;
  `}
`

const TextOnlyCloseButton = styled(TextOnlyViewButton)`
  ::before {
    opacity: 0.25;
  }
  ${media.lessThan('small')`
    color: #ffffff;
  `}
`

const TextOnlyLabel = styled.span`
  position: relative;
`

const CustomCtaWrapper = styled.div`
  display: flex;
  ${media.lessThan('small')`
    margin: 0 -1.5rem -1.5rem;
  `}
`

const overlay = {
  zIndex: 999,
  background: 'rgba(0, 0, 0, 0.8)',
}

const content = {}

const hasDeeplink = type => type !== undefined && type != null && type >= 0

function PopupModal({
  data = {},
  show,
  onClose = () => {},
  onNavigate = () => {},
  desktop,
}) {
  const {
    title,
    shortDescription,
    popupType,
    mobileImageUrl,
    desktopImageUrl,
    deeplinkType,
    customCTA,
    closeButtonTitle,
    viewButtonTitle,
  } = data

  // ST-1080 Dismiss pop-up on user click grayed out areas
  const onClickContentWrapper = () => {
    const isMobile = window.innerWidth <= screenSizes.small
    isMobile && onClose()
  }

  const onClickContent = e => {
    // prevent dismiss on click pop-up content
    e.stopPropagation()
  }

  return (
    <StyledReactModal
      isOpen={show}
      style={{ overlay, content }}
      closeTimeoutMS={150}
      // shouldCloseOnOverlayClick={window.innerWidth <= screenSizes.small}
      bodyOpenClassName="sticky-modal-open"
      htmlOpenClassName="sticky-modal-open"
    >
      <ModalContentWrapper
        type={popupType}
        desktop={desktop}
        onClick={onClickContentWrapper}
      >
        <ModalContent type={popupType} desktop={desktop}>
          {(popupType === 0 || !customCTA) && (
            <DefaultCloseButton onClick={onClose} type={popupType}>
              <Icon name="x" />
            </DefaultCloseButton>
          )}
          <Content type={popupType} desktop={desktop} onClick={onClickContent}>
            {popupType === 0 && (
              // <Image src={imageUrl} onClick={onNavigate} fullImage />
              <Image
                src={desktop ? desktopImageUrl : mobileImageUrl}
                onClick={onNavigate}
                desktop={desktop}
              />
            )}
            {popupType === 1 && (
              <React.Fragment>
                <Image src={mobileImageUrl} />
                <DetailsContainer>
                  <Title>{title}</Title>
                  <Description>{shortDescription}</Description>
                  {customCTA && (
                    <CustomCtaWrapper>
                      <BlockActionButton
                        variant="primary"
                        small
                        onClick={onNavigate}
                      >
                        {viewButtonTitle || 'View Details'}
                      </BlockActionButton>
                      <CloseButton onClick={onClose} small>
                        {closeButtonTitle || 'Close'}
                      </CloseButton>
                    </CustomCtaWrapper>
                  )}
                </DetailsContainer>
              </React.Fragment>
            )}
            {popupType === 2 && (
              <DetailsContainer>
                <TextOnlyTitle>{title}</TextOnlyTitle>
                <TextOnlyDescription>{shortDescription}</TextOnlyDescription>
                {customCTA && (
                  <CustomCtaWrapper>
                    <TextOnlyViewButton
                      variant="primary"
                      small
                      onClick={onNavigate}
                    >
                      <TextOnlyLabel>
                        {viewButtonTitle || 'View Details'}
                      </TextOnlyLabel>
                    </TextOnlyViewButton>
                    <CustomCtaSpacer />
                    <TextOnlyCloseButton onClick={onClose} small>
                      <TextOnlyLabel>
                        {closeButtonTitle || 'Close'}
                      </TextOnlyLabel>
                    </TextOnlyCloseButton>
                  </CustomCtaWrapper>
                )}
              </DetailsContainer>
            )}
          </Content>
          {popupType === 0 && hasDeeplink(deeplinkType) && (
            <FullImageNote>Click the card to view details</FullImageNote>
          )}
        </ModalContent>
      </ModalContentWrapper>
    </StyledReactModal>
  )
}

PopupModal.defaultProps = {}

PopupModal.propTypes = {}

export default PopupModal
